import clsx from 'clsx';
import * as React from 'react';

import type * as api from '@gitbook/api-client';
import type { PortalToggleableRef } from '@gitbook/react-uikit';
import { intlFormatList } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import type { ButtonProps } from '@gitbook/spine-react';
import {
    Button,
    Emoji,
    EmojiPickerButton,
    LoadingIndicator,
    LoadingIndicatorIcon,
} from '@gitbook/spine-react';
import { useLoadingState } from '@gitbook/util-react-hooks';
import { filterOutNullable } from '@gitbook/util-ts';

import { useLoggedInUser } from '../../../state';
import type { useCommentActions } from '../useCommentActions';

type CommentActions = ReturnType<typeof useCommentActions>;

export function CommentEmojiActions(props: {
    comment: api.Comment | api.CommentReply;
    commentActions: CommentActions;
    opened?: boolean;
}): React.ReactElement {
    const { comment, commentActions, opened } = props;

    return (
        <div
            className={clsx(
                'flex flex-row flex-wrap items-center justify-start gap-1 transition-opacity',
                comment.reactions.length > 0 || opened
                    ? 'opacity-100'
                    : 'opacity-0 group-focus-within/message:opacity-100 group-hover/message:opacity-100'
            )}
        >
            <AddEmojiReactionButton kind="blank" onReactionAdded={commentActions.onReact} />
            {getEmojiReactions({
                reactions: comment.reactions,
                onReactionAdded: commentActions.onReact,
                onReactionRemoved: commentActions.onUnreact,
            })}
        </div>
    );
}

/**
 * Button to pick an emoji reaction.
 */
export function AddEmojiReactionButton(
    props: {
        onReactionAdded: (emoji: string) => Promise<void>;
    } & Pick<ButtonProps, 'tooltip' | 'kind'>
): React.ReactElement {
    const { onReactionAdded, kind = 'blank', tooltip } = props;
    const [loading, wrapLoading] = useLoadingState();

    const portalRef = React.useRef<PortalToggleableRef>(null);

    /** User picked an emoji */
    const onPick: (emoji: string) => Promise<void> = async (emoji) => {
        if (portalRef.current) {
            portalRef.current.close();
        }

        if (onReactionAdded) {
            return wrapLoading(async () => {
                return onReactionAdded(emoji);
            });
        }
    };

    return (
        <EmojiPickerButton
            leadingIcon={loading ? LoadingIndicator : Icon.EmojiAddReaction}
            kind={kind}
            size="small"
            label="Add reaction"
            iconOnly
            onPick={onPick}
            disabled={loading}
            tooltip={tooltip}
        />
    );
}

/**
 * Listing of emoji reactions.
 */
export function getEmojiReactions({
    reactions,
    onReactionAdded,
    onReactionRemoved,
}: {
    reactions: api.EmojiReaction[];
    onReactionAdded: (emoji: string) => Promise<void>;
    onReactionRemoved: (emoji: string) => Promise<void>;
}): React.ReactElement[] {
    const user = useLoggedInUser();

    return reactions.map((reaction) => {
        const isEmojiAuthor = reaction.users.some((reactedBy) => reactedBy.user.id === user.uid);

        return (
            <EmojiReactionButton
                key={reaction.emoji}
                emoji={reaction.emoji}
                users={reaction.users.map((user) => user.user)}
                isEmojiAuthor={isEmojiAuthor}
                onPress={() => {
                    return isEmojiAuthor
                        ? onReactionRemoved(reaction.emoji)
                        : onReactionAdded(reaction.emoji);
                }}
            />
        );
    });
}

/** Button for a specific emoji */
function EmojiReactionButton(props: {
    emoji: string;
    users: api.User[];
    isEmojiAuthor: boolean;
    onPress: () => Promise<void>;
}): React.ReactElement {
    const { emoji, users, isEmojiAuthor, onPress } = props;
    const [loading, wrapLoading] = useLoadingState();
    const [hovering, setHovering] = React.useState(false);

    return (
        <Button
            leadingIcon={loading ? LoadingIndicatorIcon : <Emoji code={emoji} />}
            size="small"
            kind="blank"
            active={isEmojiAuthor}
            label={hovering && isEmojiAuthor ? '' : `${users.length}`}
            tooltip={{
                tooltip: <ReactionButtonTooltip users={users} isEmojiAuthor={isEmojiAuthor} />,
            }}
            onPress={() => {
                return wrapLoading(async () => {
                    await onPress();
                });
            }}
            onMouseEnter={() => {
                setHovering(true);
            }}
            onMouseLeave={() => {
                setHovering(false);
            }}
            trailingIcon={hovering && isEmojiAuthor ? Icon.X : undefined}
            disabled={loading}
        />
    );
}

/** Text in the tooltip listing all users that have reacted. */
function ReactionButtonTooltip(props: {
    users: api.User[];
    isEmojiAuthor: boolean;
}): React.ReactElement {
    const { users, isEmojiAuthor } = props;

    return (
        <>
            {isEmojiAuthor
                ? 'You'
                : intlFormatList(
                      users.filter(filterOutNullable).map((user) => user.displayName)
                  )}{' '}
            reacted
        </>
    );
}
